import { Component, Inject, NgModule } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { startWith, tap, Subject } from 'rxjs';
import { CommonModule, AsyncPipe } from '@angular/common';
import { SuggestedAmount, suggestedAmountTypes } from '@uw/core/models/suggested-amount.model';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule, MatIcon } from '@angular/material/icon';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { AngularMaterialModule } from '@uw/shared/material.module';
import { KeyValueModel } from '@uw/core/models/keyvalue.model';
import { TargetType } from '@uw/core/models/target-type.model';
import { SuggestedAmountsEntityService } from '@uw/core/services/suggested.amounts.entity.service';

type SuggestedAmountDetailData = {
  targetTypeCode: { name: string, value: any },
  suggestedAmount: SuggestedAmount,
  entityId: string,
  languages: KeyValueModel[]
};

@Component({
    selector: 'uw-suggested-amount-detail',
    templateUrl: './suggested-amount-detail.component.html',
    styleUrls: ['./suggested-amount-detail.component.scss'],
    imports: [A11yModule, MatIconButton, MatDialogClose, MatIcon, ReactiveFormsModule, AsyncPipe, TranslateModule]
})
export class SuggestedAmountDetailComponent {

  private readonly _saveSubject: Subject<SuggestedAmount> = new Subject();

  private readonly _save$ = this._saveSubject.asObservable();

  readonly suggestedAmountTypes: typeof suggestedAmountTypes = suggestedAmountTypes;

  languages!: KeyValueModel[];
  errorMessage = null;
  readonly form = new UntypedFormGroup({
    suggestAmountId: new UntypedFormControl(null, []),
    lowerRangeAmount: new UntypedFormControl(null, [Validators.required]),
    upperRangeAmount: new UntypedFormControl(null, [Validators.required]),
    suggestedAmountType: new UntypedFormControl(null, [Validators.required]),
    firstAmount: new UntypedFormControl(null, [Validators.required]),
    secondAmount: new UntypedFormControl(null, [Validators.required]),
    thirdAmount: new UntypedFormControl(null, [Validators.required]),
    message: new UntypedFormControl(null),
    languageCode: new UntypedFormControl(null),
    targetTypeCode: new UntypedFormControl(null, [Validators.required]),
    // showMessageOnly: new UntypedFormControl(false)
  });

  private _targetTypeCode!: string;

  readonly vm$ = this._save$
    .pipe(
      tap(suggestedAmount => this._handleSave(this.data.entityId,  this.data.targetTypeCode, suggestedAmount)),
      startWith(true)
    )

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: SuggestedAmountDetailData,
    private readonly _suggestedAmountsEntityService : SuggestedAmountsEntityService,
    private readonly _dialogRef: MatDialogRef<SuggestedAmountDetailComponent>
  ) {
    this._targetTypeCode = data.targetTypeCode.value;
    this.languages = data.languages;
    if (data.suggestedAmount == null) {
      this.form.reset({
        targetTypeCode: this._targetTypeCode
      } as SuggestedAmount);
    }
    else {
      data.suggestedAmount.targetTypeCode = data.targetTypeCode.value;

      const normalized = { ...data.suggestedAmount };
      if (normalized.suggestedAmountType) {
        const v = normalized.suggestedAmountType.toLowerCase();
        normalized.suggestedAmountType = v.charAt(0).toUpperCase() + v.slice(1);
      }
      this.form.patchValue(normalized, { emitEvent: false });
    }
  }

  onSave(suggesstedAmount: any) {
    this._saveSubject.next(suggesstedAmount);
  }

  private _handleSave(entityId: any, targetType: TargetType, suggestedAmount: SuggestedAmount) {
    const payload = { ...suggestedAmount, message: suggestedAmount.message ?? '' };
    const obs$ = payload.suggestAmountId
      ? this._suggestedAmountsEntityService.updateSuggestedAmount(entityId, targetType.value, payload)
      : this._suggestedAmountsEntityService.createSuggestedAmount(entityId, targetType.value, payload)

    return obs$
      .pipe(
        tap(_ => {
         // this._selectSuggestedAmountSubject.next(null);
         // this._refreshSubject.next();

        },
        error=>{
          this.errorMessage = error.error?.error ?? error.message;
        })
      ).subscribe((res)=>{
        this._dialogRef.close(res);
      });
  }
}

@NgModule({
    exports: [
        SuggestedAmountDetailComponent
    ],
    imports: [
        A11yModule,
        CommonModule,
        ReactiveFormsModule,
        AngularMaterialModule,
        MatDialogModule,
        TranslateModule,
        MatIconModule,
        MatButtonModule,
        SuggestedAmountDetailComponent
    ]
})
export class SuggestedAmountDetailModule { }
